// Intl.DisplayNames isn't in every TS lib target / RN engine (notably
// Hermes without an Intl polyfill) — resolve it defensively so a missing
// impl degrades to "name unavailable" rather than crashing. Shared by the
// nationality / currency pickers (code → display name) and country-flag
// (which builds a reverse name → ISO map on top of the region lookup).

type DisplayNamesType = 'region' | 'currency' | 'language' | 'script';

interface DisplayNamesInstance {
  of: (code: string) => string | undefined;
}

type DisplayNamesCtor = new (
  locales: string[],
  options: { type: DisplayNamesType },
) => DisplayNamesInstance;

/**
 * Build an English `(code) => name | undefined` lookup for the given
 * `Intl.DisplayNames` type. Returns a function that always yields
 * `undefined` when `Intl.DisplayNames` is unavailable or throws, so
 * callers can fall back to code-only behavior. Intl returns the input
 * code unchanged for values it can't resolve, which we normalize to
 * `undefined`.
 *
 * Call once at module scope (one underlying instance per lookup), e.g.
 * `const countryName = createDisplayNameLookup('region');`
 */
export function createDisplayNameLookup(
  type: DisplayNamesType,
): (code: string) => string | undefined {
  const Ctor = (Intl as unknown as { DisplayNames?: DisplayNamesCtor }).DisplayNames;
  let instance: DisplayNamesInstance | null = null;
  if (Ctor) {
    try {
      instance = new Ctor(['en'], { type });
    } catch {
      instance = null;
    }
  }
  return (code: string) => {
    try {
      const name = instance?.of(code);
      return name && name !== code ? name : undefined;
    } catch {
      return undefined;
    }
  };
}
